import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CATEGORIES } from '../constants';
import { useCreateCustomerRequest } from '../hooks/useCustomerRequestMutations';
import { CustomerPicker } from './CustomerPicker';
import { DuplicateMatches } from './DuplicateMatches';
import type { AskDraft, CustomerRequestCategory } from '../types';

const NO_CATEGORY = '__none__';

/**
 * New request. While the title is typed, likely duplicates are offered instead: "Upvote this"
 * opens the existing request with the customer and note carried over, and the AM confirms
 * there (seeing who already asked), so a customer is never counted twice by accident.
 */
export function CreateRequestForm({ onDone, onUpvoteExisting }: {
  onDone: (requestId: string) => void;
  onUpvoteExisting: (requestId: string, draft: AskDraft) => void;
}) {
  const [title, setTitle] = useState('');
  const [accountId, setAccountId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<CustomerRequestCategory | null>(null);
  const create = useCreateCustomerRequest();
  const busy = create.isPending;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    create.mutate(
      { title: title.trim(), accountId, note: note.trim() || undefined, description: description.trim() || undefined, category },
      { onSuccess: (detail) => onDone(detail.id) },
    );
  };

  return (
    <form onSubmit={submit} className="p-4 space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="cr-title">Title *</Label>
        <Input
          id="cr-title"
          aria-label="Title"
          value={title}
          maxLength={200}
          autoFocus
          placeholder="What did the customer ask for?"
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>

      <DuplicateMatches
        title={title}
        disabled={busy}
        onUpvote={(m) => onUpvoteExisting(m.id, { accountId, note })}
      />

      <div className="space-y-1.5">
        <Label>Customer</Label>
        <CustomerPicker value={accountId} onChange={setAccountId} disabled={busy} />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="cr-note">In the customer's words</Label>
        <Input id="cr-note" value={note} placeholder="Optional" onChange={(e) => setNote(e.target.value)} />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="cr-description">Description</Label>
        <Textarea
          id="cr-description"
          rows={4}
          value={description}
          placeholder="What problem does it solve? Optional"
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      <div className="space-y-1.5">
        <Label>Category</Label>
        <Select value={category ?? NO_CATEGORY} onValueChange={(v) => setCategory(v === NO_CATEGORY ? null : v as CustomerRequestCategory)}>
          <SelectTrigger aria-label="Category"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={NO_CATEGORY}>No category</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="flex justify-end pt-2">
        <Button type="submit" disabled={busy || !title.trim()}>
          {create.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Create request
        </Button>
      </div>
    </form>
  );
}
